/* @flow */

import flatMap from 'lodash/flatMap';
import grid from '../../grid-preact';
import { PAGINA_INICIAL } from '../../utils/constants';
import ordenar from '../../utils/ordenar';
import { type GrupoComplemento } from '../../complementos/types';
import { type StateGridProdutos } from './types';
import { type GetState, type State } from '../../preact-app/types';
import { type Produto, type Categoria } from '../../produtos/types';
import execDynamicRegExp from '../../usuario/exec-dynamic-regexp';
import api from '../../utils/api';
import { buscarImpressao } from '../empresa';

type FiltrarQueryAction = {
    type: 'balcao/grid-produtos/FILTRAR_QUERY',
    query: string,
};

type BuscarDadosAction = {
    type: 'balcao/grid-produtos/BUSCAR_DADOS',
    produtos: Produto[],
    categorias: Categoria[],
    gruposComplemento: GrupoComplemento[],
};

type SelecionarCategoria = {
    type: 'balcao/grid-produtos/SELECIONAR_CATEGORIA',
    id: ?number,
};

type Action =
    | FiltrarQueryAction
    | SelecionarCategoria
    | BuscarDadosAction;

// Action Types

const FILTRAR_QUERY = 'balcao/grid-produtos/FILTRAR_QUERY';
const BUSCAR_DADOS = 'balcao/grid-produtos/BUSCAR_DADOS';
const SELECIONAR_CATEGORIA = 'balcao/grid-produtos/SELECIONAR_CATEGORIA';
const AVANCAR_CATEGORIA = 'balcao/grid-produtos/AVANCAR_CATEGORIA';
const VOLTAR_CATEGORIA = 'balcao/grid-produtos/VOLTAR_CATEGORIA';

// Selectors

export const getGridProdutos = (state: State) => state.balcao.gridProdutos;

export const getProdutos = (state: State) => getGridProdutos(state).produtos;

export const getGruposComplementos = (state: State) => (
    getGridProdutos(state).gruposComplemento
);

export const getProdutoPorId = (state: State, id: number) => getGridProdutos(state)
    .produtos
    .find(produto => produto.id === id);

export const getCategorias = (state: State) => getGridProdutos(state).categorias;

export const getCategoriaSelecionada = (state: State) => (
    getGridProdutos(state).categoriaSelecionada
);

// Utils

const filtroCategoria = (categoriasId: ?number) => (produto: Produto): boolean => (
    !categoriasId ? true : produto.categoriasId === categoriasId
);

// Thunks

const filtroQuery = (query: string) => (produto: Produto) => {
    if (!query) {
        return true;
    }

    return query === String(produto.id)
        || execDynamicRegExp(query, produto.nome)
        || execDynamicRegExp(query, produto.descricao);
};

const {
    reducer: gridReducer,
    actions: {
        proximaPagina,
        alterarQuantidade,
        voltarPagina,
        alterarOrdem,
        alterarItens,
        pesquisar,
        pesquisarSetAtalhos,
    },
} = grid({
    namespace: 'balcao/grid-produtos',
    getGrid: getGridProdutos,
    pesquisar: () => (dispatch: Function, getState: GetState) => {
        const {
            ordenacao,
            quantidade,
            pagina,
            query,
            categoriaSelecionada,
            produtos,
        } = getGridProdutos(getState());

        const itens = produtos
            .filter(filtroCategoria(categoriaSelecionada))
            .filter(filtroQuery(query))
            .sort(ordenar(ordenacao))
            .slice(pagina * quantidade, (pagina + 1) * quantidade);

        dispatch(alterarItens(itens));
        return Promise.resolve();
    },
});


const buscarDadosActionCreator = (
    categorias: Categoria[],
    gruposComplemento: GrupoComplemento[],
): BuscarDadosAction => ({
    type: BUSCAR_DADOS,
    categorias,
    produtos: flatMap(categorias, c => c.produtos),
    gruposComplemento,
});

export const buscarDados = () => (dispatch: Function, getState: GetState) => {
    if (getGridProdutos(getState()).produtos.length) {
        return Promise.resolve();
    }

    dispatch(buscarImpressao());

    return Promise.all([
        api.getCategorias({
            quantidade: 9999,
            ativo: 1,
            embed: 'produtos,grupos-complemento-filtrando',
            funcionamento: 1,
        }),
        api.getGruposComplemento({
            quantidade: 9999,
            ativo: 1,
            embed: 'complementos',
            ordenacao: 'ordem',
        }),
    ])
        .then(([
            { data: categorias },
            { data: { data: gruposComplemento } },
        ]) => {
            dispatch(buscarDadosActionCreator(categorias, gruposComplemento));
        });
};

export const filtrarQuery = (query: string) => (dispatch: Function) => {
    dispatch({
        type: FILTRAR_QUERY,
        query,
    });
    dispatch(pesquisar());
};

export const selecionarCategoria = (id: number) => (dispatch: Function) => {
    dispatch({
        type: SELECIONAR_CATEGORIA,
        id,
    });
    dispatch(pesquisar());
};

export const avancarCategoria = () => (dispatch: Function) => {
    dispatch({
        type: AVANCAR_CATEGORIA,
    });
    dispatch(pesquisar());
};

export const voltarCategoria = () => (dispatch: Function) => {
    dispatch({
        type: VOLTAR_CATEGORIA,
    });
    dispatch(pesquisar());
};

export {
    proximaPagina,
    alterarQuantidade,
    voltarPagina,
    alterarOrdem,
    pesquisar,
    pesquisarSetAtalhos,
};

const initialState = {
    pagina: PAGINA_INICIAL,
    quantidade: 10,
    ordenacao: 'id DESC',
    query: '',
    permitirProximaPagina: true,
    itens: [],
    categorias: [],
    gruposComplemento: [],
    produtos: [],
    categoriaSelecionada: 0,
};

const gridProdutosReducer = (state: StateGridProdutos = initialState, action: Action) => {
    switch (action.type) {
    case BUSCAR_DADOS: {
        const gruposComplemento: GrupoComplemento[] = action
            .gruposComplemento
            .filter(grupo => grupo.complementos.length > 0);

        return {
            ...state,
            categorias: action.categorias,
            produtos: action.produtos,
            gruposComplemento,
        };
    }
    case SELECIONAR_CATEGORIA:
        return {
            ...state,
            categoriaSelecionada: action.id,
            pagina: PAGINA_INICIAL,
        };
    case FILTRAR_QUERY:
        return {
            ...state,
            query: action.query,
            pagina: PAGINA_INICIAL,
        };
    case AVANCAR_CATEGORIA: {
        const map = state.categorias.map(({ id }) => id);
        return {
            ...state,
            categoriaSelecionada: map[map.indexOf(state.categoriaSelecionada) + 1],
        };
    }
    case VOLTAR_CATEGORIA: {
        const map = state.categorias.map(({ id }) => id);
        const categoriaSelecionada = map[map.indexOf(state.categoriaSelecionada) - 1];
        return {
            ...state,
            categoriaSelecionada,
        };
    }
    default:
        return gridReducer(state, action);
    }
};

export default gridProdutosReducer;
